import React, { useState, useEffect } from "react";
import { Send, Clock, MapPin } from "lucide-react";
import { motion } from "framer-motion";

export default function ContactForm() {
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const hubs = [
    {
      id: "HYD",
      city: "Hyderabad",
      country: "India",
      address:
        "Mahaveer The Watermark, Whitefields, HITEC City, Telangana 500081",
      timezone: "Asia/Kolkata",
      status: "Engineering HQ",
      tag: "CORE_DEV_HUB",
    },
    {
      id: "CHI",
      city: "Chicago",
      country: "United States",
      address: "1701 E Woodfield Rd, Schaumburg, IL 60173",
      timezone: "America/Chicago",
      status: "Enterprise Client Services",
      tag: "AMER_OPS_GATEWAY",
    },
    {
      id: "AMS",
      city: "Amsterdam",
      country: "Netherlands",
      address: "Radarweg 29, 1043 NX, Amsterdam",
      timezone: "Europe/Amsterdam",
      status: "EMEA Delivery Operations",
      tag: "EMEA_NODE",
    },
  ];

  const getCurrentTime = (timezone) => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(currentDate);
    } catch {
      return "--:--:--";
    }
  };

  return (
    <section className="relative w-full bg-[#F8FAFC] py-24 px-6 sm:px-8 lg:px-12 border-b border-slate-200/80 font-sans overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(#0f172a 1px, transparent 1px)`,
          backgroundSize: "28px 28px",
        }}
      />
      <div className="max-w-7xl mx-auto relative z-10 space-y-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 space-y-6 flex flex-col justify-between h-full">
            <div className="space-y-3">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.05 }}
                className="inline-flex items-center gap-2 px-0.5 py-1.5 backdrop-blur-md shadow-xs"
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
                  GLOBAL ENGAGEMENT
                </span>
              </motion.div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
                Let’s{" "}
                <span className="italic font-serif font-normal text-[#5dc9f0]">
                  Collaborate
                </span>
              </h2>
            </div>
            <div className="space-y-3">
              {hubs.map((hub) => {
                return (
                  <div
                    key={hub.id}
                    className={`rounded-2xl p-5 relative overflow-hidden border transition-all duration-300 `}
                  >
                    <div
                  className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
                  style={{
                    background:
                      "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
                  }}
                />
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2.5">
                        <div className="h-10 w-10 rounded-xl bg-white border border-slate-300 text-slate-950 flex items-center justify-center shadow-xs">
                          <MapPin size={22} />
                        </div>
                        <span className="text-lg font-semibold font-serif text-[#000036]">
                          {hub.city}, {hub.country}
                        </span>
                      </div>
                      <span className="font-mono text-sm font-bold tracking-wider px-2 py-0.5 rounded-md bg-[#5dc9f0] text-white">
                        {hub.id}
                      </span>
                    </div>
                    <p className="text-base text-slate-500 leading-relaxed pl-4.5">
                      {hub.address}
                    </p>
                    <div className="flex items-center justify-end gap-2 mt-1">
                      <Clock className="w-4 h-4 text-[#000000]" />
                      <span className="font-mono text-sm font-extrabold text-[#000036] ">
                        {getCurrentTime(hub.timezone)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white border border-slate-200/90 p-6 sm:p-8 shadow-sm relative overflow-hidden">
    
              <div className="relative z-10 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#000036] tracking-tight">
                    Start an Engineering Engagement
                  </h3>
                  <p className="text-sm text-slate-500 tracking-wider mt-1">
                    Submit your architecture scope or legacy migration
                    requirements
                  </p>
                </div>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-4"
                >
          
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="John"
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm focus:bg-white focus:border-slate-950 focus:ring-0 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Doe"
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm focus:bg-white focus:border-slate-950 focus:ring-0 outline-none transition-all"
                      />
                    </div>
                  </div>

    
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Business Email *
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="j.doe@enterprise.com"
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm focus:bg-white focus:border-slate-950 focus:ring-0 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        placeholder="+91 00000-00000"
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm focus:bg-white focus:border-slate-950 focus:ring-0 outline-none transition-all"
                      />
                    </div>
                  </div>

        
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Organization / Enterprise *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Acme Financial Corp"
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm focus:bg-white focus:border-slate-950 focus:ring-0 outline-none transition-all"
                    />
                  </div>

        
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Scope / Architecture Notes *
                    </label>
                    <textarea
                      rows={4}
                      required
                      placeholder="Outline target workflows, legacy platform (e.g. Pega, IBM BPM), and timeline..."
                      className="w-full p-4 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm focus:bg-white focus:border-slate-950 focus:ring-0 outline-none transition-all resize-none"
                    />
                  </div>

                 
                  <div className="pt-2">
                    
                    <button
                  type="submit"
                  className="group w-auto  relative inline-flex items-center justify-center md:justify-between gap-3 md:px-5 px-3 py-2 border border-black/15 bg-white text-slate-900 font-mono text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-slate-950 hover:text-white hover:shadow-lg rounded-full"
                >
                  <span>Submit Request</span>
                  <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1 group-hover:bg-white group-hover:text-slate-950">
                    <Send size={15} />
                  </div>
                </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
